import { useCallback, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  Gauge,
  MapPin,
  AlertTriangle,
  WifiOff,
  MessageSquare,
  CircleQuestionMark,
  GitCompareArrows,
  FolderCog,
  Tags,
  LogOut,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils.js";
import { logOut } from "@/api/detechtion-account-api";
import TenantSwitcher from "@/components/tenant-switcher.jsx";
import { Button } from "@/components/ui/button.jsx";
import Cookies from "js-cookie";
import { useSessionTenant } from "@/contexts/SessionTenantContext.jsx";
import { useUserAccess } from "@/hooks/useUserAccess.js";
import { SidebarPortalProvider } from "@/components/sidebar-portal.jsx";
import {
  sidebarNavRail,
  sidebarNavSelected,
} from "@/lib/status-styles.js";
import {
  PENDO_CONTACT_SUPPORT,
  PENDO_SUBMIT_FEEDBACK,
} from "@/lib/pendo-targets.js";

/**
 * Navigation configuration for the sidebar
 */
const baseNavigation = [
  { name: "Fleet Overview", href: "/", icon: Gauge },
  { name: "Fleet Map", href: "/map", icon: MapPin },
  { name: "Downtime Events", href: "/downtime", icon: AlertTriangle },
  { name: "No-Data Report", href: "/no-data-report", icon: WifiOff },
  {
    name: "Operating Points",
    href: "/operating-points",
    icon: GitCompareArrows,
  },
];

const classificationNavItem = {
  name: "Classification",
  href: "/classification",
  icon: Tags,
};

const manageTagGroupsNavItem = {
  name: "Manage Tag Groups",
  href: "/manage-groups",
  icon: FolderCog,
};

const LAUNCHPAD_URL = import.meta.env.VITE_LAUNCHPAD_URL;

const USERNAME = Cookies.get("DetechtionAuth.Username");

/**
 * Sidebar component with responsive navigation
 * Features mobile menu toggle and navigation links
 * @param {{isMobileMenuOpen?: boolean, setIsMobileMenuOpen?: Function}} props - Sidebar props
 * @returns {JSX.Element} Sidebar component with navigation
 */
export default function Sidebar({
  isMobileMenuOpen = false,
  setIsMobileMenuOpen = () => {},
}) {
  /** @type {[string, Function]} */
  const [location] = useLocation();
  const { canManageTagGroups, isLoadingManageTagGroupTenants } =
    useSessionTenant();
  const { isOneViewAdmin, isLoading: isLoadingUserAccess } = useUserAccess();
  const [portalContainer, setPortalContainer] = useState(
    /** @type {HTMLElement | null} */ (null)
  );
  const [monitoringOpen, setMonitoringOpen] = useState(true);

  const setSidebarRef = useCallback((node) => {
    setPortalContainer(node);
  }, []);

  const navigation = [
    ...baseNavigation,
    ...(!isLoadingUserAccess && isOneViewAdmin
      ? [classificationNavItem]
      : []),
    ...(!isLoadingManageTagGroupTenants && canManageTagGroups
      ? [manageTagGroupsNavItem]
      : []),
  ];

  const handleLogOut = () => {
    logOut(() => {
      window.location.href = `${LAUNCHPAD_URL}/?redirect_uri=${encodeURIComponent(window.location.href)}`;
    });
  };

  return (
    <SidebarPortalProvider portalContainer={portalContainer}>
      {/* Sidebar */}
      <div
        ref={setSidebarRef}
        className={cn(
          "lg:static w-64 bg-sidebar border-r border-sidebar-border flex flex-col h-full min-h-0 transition-all duration-300 ease-in-out will-change-auto",
          isMobileMenuOpen
            ? "fixed top-0 left-0 z-panel h-screen"
            : "hidden lg:flex",
          "lg:z-auto"
        )}>
        <div className='shrink-0 px-3 pt-3 pb-3'>
          <TenantSwitcher />
        </div>

        <nav className='flex-1 min-h-0 overflow-y-auto px-3 pb-4'>
          <button
            type='button'
            className='flex w-full items-center justify-between px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'
            onClick={() => setMonitoringOpen((open) => !open)}
            aria-expanded={monitoringOpen}>
            <span>Monitoring</span>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 text-muted-foreground transition-transform",
                !monitoringOpen && "-rotate-90"
              )}
            />
          </button>

          {monitoringOpen && (
            <div className='mt-1 space-y-0.5'>
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location === item.href;
                return (
                  <Link key={item.name} href={item.href}>
                    <div
                      className={cn(
                        "relative flex items-center px-3 py-2.5 rounded-md transition-colors cursor-pointer",
                        isActive
                          ? sidebarNavSelected
                          : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                      )}
                      onClick={() => {
                        if (window.innerWidth < 1024) {
                          setIsMobileMenuOpen(false);
                        }
                      }}
                      data-active={isActive}>
                      {isActive && (
                        <span className={sidebarNavRail} aria-hidden='true' />
                      )}
                      <Icon className='mr-3 shrink-0' size={18} />
                      <span className='text-sm font-normal'>{item.name}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </nav>

        {/* Pinned footer */}
        <div className='shrink-0 border-t border-sidebar-border px-3 py-3 space-y-3'>
          <div className='space-y-2'>
            {/*
              Pendo targets — ids / size / className / labels come from
              @/lib/pendo-targets.js. Do not rename or restyle without a Pendo update.
            */}
            <div>
              <Button
                variant='outline'
                size={PENDO_SUBMIT_FEEDBACK.size}
                id={PENDO_SUBMIT_FEEDBACK.id}
                className={PENDO_SUBMIT_FEEDBACK.className}>
                <MessageSquare className='mr-3' size={16} />
                {PENDO_SUBMIT_FEEDBACK.label}
              </Button>
            </div>
            <div>
              <Button
                variant='outline'
                size={PENDO_CONTACT_SUPPORT.size}
                id={PENDO_CONTACT_SUPPORT.id}
                className={PENDO_CONTACT_SUPPORT.className}>
                <CircleQuestionMark className='mr-3' size={16} />
                {PENDO_CONTACT_SUPPORT.label}
              </Button>
            </div>
          </div>

          <div className='px-1 pt-1 space-y-2'>
            <div>
              <p className='text-xs font-light text-muted-foreground'>
                Logged in as
              </p>
              <p
                className='text-sm font-bold text-foreground truncate'
                title={USERNAME || undefined}>
                {USERNAME || "-"}
              </p>
            </div>
            <button
              type='button'
              onClick={handleLogOut}
              className='inline-flex items-center gap-2 text-sm font-medium text-destructive hover:opacity-80 transition-opacity'>
              <LogOut size={15} />
              Log Out
            </button>
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className='fixed inset-0 bg-foreground/50 lg:hidden z-backdrop'
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </SidebarPortalProvider>
  );
}
